import Card from "@/components/card"
import { useRisksSpace } from "@/core/risks-space.context"
import { useAppSelector } from "@/core/root-reducer"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import { PreventionAction } from "@/entities/prevention-action/model/prevention-action.types"
import PreventionActionChecklistItem from "@/entities/prevention-action/ui/PreventionActionChecklistItem"
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons"
import DashboardCardTitle, { DASHBOARD_MAX_ITEMS } from "./DashboardCardTitle"
import ComingSoonModal from "@/modals/coming-soon.modal"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

//Hauteur d'une mesure rendue par PreventionActionChecklistItem : min-h 46 + py 2x10 + bordure 2x1
const ACTION_HEIGHT = 68
const ACTION_GAP = 10
const MIN_BOTTOM_PADDING = 18

function getStartDate(action: PreventionAction) {
  const value = action.dateStartedPlanned ?? action.datePlanned
  return value ? new Date(value) : null
}

export default function EstablishmentCalendarOverview({ onCapacityChange }: { onCapacityChange?: (canFit: boolean) => void }) {
  const navigate = useNavigate()
  //Le calendrier n'existe que dans l'app : en lecture seule, la carte n'est pas un lien.
  const { isReadonly } = useRisksSpace()
  const actions = useAppSelector(preventionActionStore.selectors.list)
  const actionsContainerRef = useRef<HTMLDivElement>(null)
  const [availableActionCount, setAvailableActionCount] = useState(2)
  const [isComingSoon, setIsComingSoon] = useState(false)
  const upcomingActions = useMemo(
    () =>
      actions
        .filter((action) => action.cStatus !== "done")
        .sort((first, second) => {
          const firstDate = getStartDate(first)
          const secondDate = getStartDate(second)
          // Les mesures datées restent prioritaires ; celles à planifier complètent la place disponible.
          if (firstDate && !secondDate) return -1
          if (!firstDate && secondDate) return 1
          if (firstDate && secondDate) {
            const dateDifference = firstDate.getTime() - secondDate.getTime()
            if (dateDifference !== 0) return dateDifference
          }
          return (second.cRiskValueEffective ?? -1) - (first.cRiskValueEffective ?? -1)
        }),
    [actions]
  )

  useEffect(() => {
    const container = actionsContainerRef.current
    if (!container) return

    const updateAvailableActionCount = () => {
      const usableHeight = Math.max(0, container.clientHeight - MIN_BOTTOM_PADDING)
      const count = Math.floor((usableHeight + ACTION_GAP) / (ACTION_HEIGHT + ACTION_GAP))
      setAvailableActionCount(Math.max(0, count))
      onCapacityChange?.(count > 0)
    }

    updateAvailableActionCount()
    const observer = new ResizeObserver(updateAvailableActionCount)
    observer.observe(container)
    return () => observer.disconnect()
  }, [onCapacityChange])

  return (
    <Card
      onClick={isReadonly ? undefined : () => navigate("/risks/calendar")}
      className={`${isReadonly ? "" : "_hover "}mb-0 flex min-w-0 flex-1 flex-col overflow-hidden [&>.page-loader]:min-h-0 [&>.page-loader]:flex-1 [&>.page-loader>div]:flex [&>.page-loader>div]:min-h-0 [&>.page-loader>div]:min-w-0 [&>.page-loader>div]:flex-1`}
      title={
        <DashboardCardTitle
          icon={faCalendarDays}
          title="Prévention"
          onDetailClick={() =>
            isReadonly ? setIsComingSoon(true) : navigate("/risks/calendar")
          }
        />
      }>
      {isComingSoon && <ComingSoonModal onClose={() => setIsComingSoon(false)} />}
      {/* La carte entiere mene au calendrier : les mesures gardent leur clic pour elles, chacune
          ouvrant son wizard via PreventionActionChecklistItem. */}
      <div
        ref={actionsContainerRef}
        className="flex min-h-0 min-w-0 flex-1 flex-col justify-center gap-10 overflow-hidden"
        onClick={(event) => event.stopPropagation()}>
        {upcomingActions.length === 0 && <div className="grey-t text-center">Aucune mesure de prévention à réaliser.</div>}
        {upcomingActions.slice(0, Math.min(availableActionCount, DASHBOARD_MAX_ITEMS)).map((action) => (
          <PreventionActionChecklistItem key={action.id} action={action} isReadonly={isReadonly} />
        ))}
      </div>
    </Card>
  )
}
